import {DOCUMENT} from '@angular/common';
import { Directive, effect, input, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import {Router} from '@angular/router';
import {StorageKeys} from '@app/core/constants/storage-keys';
import {PlatformStorage} from '@app/core/tokens/platform-storage.token';
import {OidcSecurityService, OpenIdConfiguration} from 'angular-auth-oidc-client';
import {filter, switchMap, tap} from 'rxjs';

@Directive({
  selector: '[appLogoutAfterIdleTime]',
  standalone: true,
})
export class LogoutAfterIdleTimeDirective implements OnInit, OnDestroy {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly storage = inject(PlatformStorage);
  private readonly router = inject(Router);

  readonly timerInHHmmss = input<string>('');

  private static timeString: string;
  private static logoutTimerSeconds: number;
  clearTime!: NodeJS.Timeout;
  window: (Window & typeof globalThis) | null;

  constructor() {
    effect(() => {
      const timeString = this.timerInHHmmss();

      if (!timeString) return;

      LogoutAfterIdleTimeDirective.timeString = timeString;
      LogoutAfterIdleTimeDirective.logoutTimerSeconds = LogoutAfterIdleTimeDirective.setLogoutTimerInSeconds();
    });
    this.window = this.document.defaultView;
    LogoutAfterIdleTimeDirective.logoutTimerSeconds = LogoutAfterIdleTimeDirective.setLogoutTimerInSeconds();
  }

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.setLogoutTimer();
      this.setOnStorageChangeListener();
      this.setOnClickListener();
      this.setScrollListener();
      this.setMouseMoveListener();
      this.setOnKeydownListener();
      this.setKeyDownListener();
    });
  }

  private setOnStorageChangeListener(): void {
    this.window?.addEventListener('storage', (storageEvent: StorageEvent) => {
      if (storageEvent.storageArea === localStorage) {
        this.redirectToLoginIfNotAuthenticated(storageEvent);
      }
    });
  }

  private setOnClickListener(): void {
    this.window?.addEventListener('mousedown', () => {
      this.resetLogoutTimer();
    });
  }

  private setScrollListener(): void {
    this.window?.addEventListener('scroll', () => {
      this.resetLogoutTimer();
    });
  }

  private setMouseMoveListener(): void {
    this.window?.addEventListener('mousemove', () => {
      this.resetLogoutTimer();
    });
  }

  private setOnKeydownListener(): void {
    this.document.addEventListener('keydown.enter', () => {
      this.resetLogoutTimer();
    });
  }

  private setKeyDownListener(): void {
    this.window?.addEventListener('keydown', () => {
      this.resetLogoutTimer();
    });
  }

  private static setLogoutTimerInSeconds() {
    const oneSecond = 1000;
    const timeString = LogoutAfterIdleTimeDirective.timeString || '00:05:20';
    const time = timeString.split(':');
    const hours = time.length > 0 ? parseInt(time[0]) : 0;
    const minutes = time.length > 1 ? parseInt(time[1]) : 0;
    const seconds = time.length > 2 ? parseInt(time[2]) : 0;
    return hours * 60 * 60 * oneSecond + minutes * 60 * oneSecond + seconds * oneSecond;
  }

  private resetLogoutTimer(): void {
    LogoutAfterIdleTimeDirective.logoutTimerSeconds = LogoutAfterIdleTimeDirective.setLogoutTimerInSeconds();
  }

  private setLogoutTimer(): void {
    this.clearTime = setInterval(() => {
      LogoutAfterIdleTimeDirective.logoutTimerSeconds -= 1000;
      if (LogoutAfterIdleTimeDirective.logoutTimerSeconds <= 0) {
        this.oidcSecurityService.logoffAndRevokeTokens().subscribe(() => {
          this.storage.removeItem(StorageKeys.accessToken);
        });
        clearInterval(this.clearTime);
      }
    }, 1000);
  }

  private redirectToLoginIfNotAuthenticated(storageEvent: StorageEvent): void {
    this.oidcSecurityService
      .getConfiguration()
      .pipe(
        filter((config: OpenIdConfiguration | null) => storageEvent.key === config?.configId),
        switchMap(() => this.oidcSecurityService.isAuthenticated()),
        filter((isAuthenticated: boolean) => !isAuthenticated),
        tap(() => this.router.navigate(['/my-orders']))
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    clearTimeout(this.clearTime);
  }
}
